import React, { useState } from 'react';
import { graphqlApi } from '../../services/graphql';
import { useAuthStore } from '../../store/authStore';
import { toast } from 'react-hot-toast';

export const ProfileExistenceDebug: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [debugResults, setDebugResults] = useState<any>(null);

  const debugProfileExistence = async () => {
    if (!user?.userId) {
      toast.error('Please login first');
      return;
    }

    setLoading(true);
    setDebugResults(null);

    try {
      console.log('🔍 [PROFILE DEBUG] Starting profile existence debug for user:', user.userId);

      // Step 1: Check what user data we have
      const userData = {
        userId: user.userId,
        email: user.email,
        userType: user.userType,
        authState: !!user
      };
      console.log('🔍 [PROFILE DEBUG] User data:', userData);

      // Step 2: Try to get influencer profile
      console.log('🔍 [PROFILE DEBUG] Calling getInfluencerByUserId...');
      const influencerResponse = await graphqlApi.getInfluencerByUserId(user.userId);
      console.log('🔍 [PROFILE DEBUG] Influencer response:', influencerResponse);

      // Step 4: Check GraphQL query directly (raw)
      const rawInfluencerQuery = `
        query GetInfluencerByUserId($userId: ID!) {
          getInfluencerByUserId(userId: $userId) {
            success
            message
            error
            data {
              influencerId
              userId
              name
              email
              bio
              profileImage
              mainCategory
              categories
              followerCounts {
                instagram
                youtube
                tiktok
                twitter
              }
              socialMediaHandles {
                instagram
                youtube
                tiktok
                twitter
              }
              location
              languages
              portfolio
              availableFrom
              availableTo
              ratePerPost
              engagementRate
              rating
              completedCampaigns
              isVerified
              createdAt
              updatedAt
            }
          }
        }
      `;

      console.log('🔍 [PROFILE DEBUG] Testing raw GraphQL query...');

      const debugResults = {
        userData,
        influencerResponse,
        rawQuery: rawInfluencerQuery,
        timestamp: new Date().toISOString()
      };

      setDebugResults(debugResults);

      // Analysis
      if (influencerResponse?.success && influencerResponse?.data) {
        console.log('✅ [PROFILE DEBUG] Influencer profile found successfully');
        toast.success('Profile found! Check debug results for details.');
      } else if (!influencerResponse?.success) {
        console.log('❌ [PROFILE DEBUG] Profile retrieval failed:', influencerResponse?.error || 'Unknown error');
        toast.error(`Profile retrieval failed: ${influencerResponse?.error || 'Unknown error'}`);
      } else {
        console.log('⚠️ [PROFILE DEBUG] No profile data returned (profile might not exist)');
        toast.error('No profile data returned - profile might not exist for this user');
      }

    } catch (error) {
      console.error('🔍 [PROFILE DEBUG] Error during debug:', error);
      setDebugResults({
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
      toast.error('Debug failed - check console for details');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-yellow-800">Please login to use the Profile Existence Debug Tool</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg max-w-6xl mx-auto">
      <h2 className="text-xl font-bold mb-4">🔍 Profile Existence Debug Tool</h2>

      <div className="mb-4 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Issue:</strong> Profile exists in database but "My Profile" shows setup form
        </p>
        <p className="text-sm text-blue-800">
          <strong>Database Profile UserId:</strong> c458a418-f091-707d-8952-2e5b5ca15b93
        </p>
        <p className="text-sm text-blue-800">
          <strong>Current User:</strong> {user.email} (ID: {user.userId})
        </p>
        <p className="text-sm text-blue-800">
          <strong>User Type:</strong> {user.userType}
        </p>
      </div>

      <button
        onClick={debugProfileExistence}
        disabled={loading}
        className="mb-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
      >
        {loading ? 'Debugging...' : 'Debug Profile Existence'}
      </button>

      {debugResults && (
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">📊 Debug Results:</h3>
            <pre className="bg-white p-4 rounded border text-sm overflow-auto max-h-96">
              {JSON.stringify(debugResults, null, 2)}
            </pre>
          </div>

          {debugResults.influencerResponse && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">🧠 Analysis:</h3>
              <div className="text-sm space-y-2">
                <div className="flex items-center space-x-2">
                  <span className={`w-3 h-3 rounded-full ${debugResults.influencerResponse.success ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  <span>API Call Success: {debugResults.influencerResponse.success ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`w-3 h-3 rounded-full ${debugResults.influencerResponse.data ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  <span>Profile Data Returned: {debugResults.influencerResponse.data ? 'Yes' : 'No'}</span>
                </div>
                {debugResults.influencerResponse.error && (
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    <span>Error: {debugResults.influencerResponse.error}</span>
                  </div>
                )}
                {debugResults.influencerResponse.data?.influencerId && (
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    <span>Profile ID: {debugResults.influencerResponse.data.influencerId}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">🔧 Next Steps:</h3>
            <ol className="text-sm space-y-1 list-decimal list-inside">
              <li>Check if the API call is successful but returns no data</li>
              <li>Verify the userId matches what's stored in the database</li>
              <li>Check if the profile exists but with a different userId</li>
              <li>Examine the frontend profile detection logic</li>
              <li>Test the GraphQL query parameters</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};
